import { Trans } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { Button } from "~/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "~/components/ui/card";
import { useAuthLogoutForm } from "~/forms/auth/logout";
import { cn } from "~/lib/utils";
import { useTRPC } from "~/trpc/client";

export function useRefreshSession() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const router = useRouter();
	const navigate = useNavigate();

	const refreshMutation = useMutation(
		trpc.auth.refresh.mutationOptions({
			async onSuccess() {
				// Automatically refresh queries after a successful refresh
				await queryClient.invalidateQueries();

				await router.invalidate();
			},
			async onError() {
				// Clear all queries and redirect to login on error
				queryClient.clear();

				await navigate({ to: "/auth/login" });
			},
		}),
	);

	async function refreshSession() {
		await refreshMutation.mutateAsync();
	}

	return {
		refreshSession,
	};
}

export function SessionExpiredForm({
	className,
	hintEmail,
	redirect,
	...props
}: React.ComponentPropsWithoutRef<"div"> & {
	hintEmail?: string;
	redirect?: string;
}) {
	const logoutForm = useAuthLogoutForm();
	const { refreshSession } = useRefreshSession();

	return (
		<div className={cn("flex flex-col gap-6", className)} {...props}>
			<Card>
				<CardHeader className="text-center">
					<CardTitle className="text-xl">
						<Trans>Session expired</Trans>
					</CardTitle>
					<CardDescription>
						<Trans>Your session expired, do you want to stay connected?</Trans>
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid gap-6">
						<div className="grid gap-6">
							<Button onClick={() => refreshSession()}>
								<Trans>Stay connected</Trans>
							</Button>
						</div>
						<div className="text-center text-sm">
							<logoutForm.AppForm>
								<form
									className="contents"
									onSubmit={(e) => {
										e.preventDefault();
										e.stopPropagation();
										logoutForm.handleSubmit();
									}}
								>
									<logoutForm.FormButton type="submit" variant="link">
										<Trans>Sign out</Trans>
									</logoutForm.FormButton>
								</form>
							</logoutForm.AppForm>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

export function SessionExpiredToast({
	className,
	hintEmail,
	redirect,
	...props
}: React.ComponentPropsWithoutRef<"div"> & {
	hintEmail?: string;
	redirect?: string;
}) {
	const logoutForm = useAuthLogoutForm();
	const { refreshSession } = useRefreshSession();

	return (
		<div className={cn("flex flex-col gap-2", className)} {...props}>
			<p>
				<Trans>Your session expired, do you want to stay connected?</Trans>
			</p>
			<div className="flex items-center gap-4">
				<Button size="sm" onClick={() => refreshSession()}>
					<Trans>Stay connected</Trans>
				</Button>

				<logoutForm.AppForm>
					<form
						className="contents"
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							logoutForm.handleSubmit();
						}}
					>
						<logoutForm.FormButton size="sm" type="submit" variant="link">
							<Trans>Sign out</Trans>
						</logoutForm.FormButton>
					</form>
				</logoutForm.AppForm>
			</div>
		</div>
	);
}
